import { injectAesthetic } from './shared-styles.ts';

export class AppMeter extends HTMLElement {
  static get observedAttributes() {
    return ['value', 'max', 'label', 'sublabel', 'color'];
  }

  private seed: number;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.seed = Math.floor(Math.random() * 10000);
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  render() {
    const value = parseFloat(this.getAttribute('value') || '0');
    const max = parseFloat(this.getAttribute('max') || '100');
    const label = this.getAttribute('label') || '';
    const sublabel = this.getAttribute('sublabel') || '';
    const color = this.getAttribute('color') || 'var(--accent, #f59e0b)';

    const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
      ${injectAesthetic(this.seed)}
      <style>
        :host {
          display: block;
          font-family: var(--font-main);
        }
        .meter-container {
          position: relative;
          width: 120px;
          height: 120px;
          display: grid;
          place-items: center;
        }
        svg.meter-svg {
          transform: rotate(-90deg);
          width: 100%;
          height: 100%;
          overflow: visible;
        }
        circle {
          fill: none;
          stroke-width: 8;
          cx: 60;
          cy: 60;
          r: ${radius};
          stroke-linecap: round;
        }
        .bg {
          stroke: rgba(255, 255, 255, 0.08);
          stroke-width: 10;
          filter: url(#meter-sketch-${this.seed});
        }
        .progress {
          stroke: ${color};
          stroke-dasharray: ${circumference};
          stroke-dashoffset: ${offset};
          transition: stroke-dashoffset 1s ease-out;
          filter: url(#meter-sketch-${this.seed});
          stroke-width: 10;
        }
        .scribble-1 {
          stroke: rgba(0, 0, 0, 0.2);
          stroke-dasharray: 3 4;
          stroke-dashoffset: ${offset};
          stroke-width: 8;
          filter: url(#meter-sketch-${this.seed});
          stroke-linecap: round;
        }
        .scribble-2 {
          stroke: rgba(255, 255, 255, 0.1);
          stroke-dasharray: 2 6;
          stroke-dashoffset: ${offset};
          stroke-width: 6;
          transform-origin: 60px 60px;
          transform: rotate(10deg);
          stroke-linecap: round;
        }
        .content {
          position: absolute;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .value {
          font-size: 1.25rem;
          font-weight: 800;
          color: var(--text-main);
          letter-spacing: -0.02em;
        }
        .label {
          font-size: 0.75rem;
          color: var(--text-muted);
          margin-top: 0.25rem;
        }
        .sub {
          font-size: 0.65rem;
          color: var(--text-dim);
        }
      </style>
      <div class="meter-container">
        <svg class="meter-svg" viewBox="0 0 120 120">
          <defs>
            <filter id="meter-sketch-${this.seed}" x="-5%" y="-5%" width="110%" height="110%">
              <feTurbulence type="turbulence" baseFrequency="0.025" numOctaves="3" seed="${this.seed}" result="noise" />
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="2" xChannelSelector="R" yChannelSelector="G" />
            </filter>
          </defs>
          <circle class="bg" />
          <circle class="progress" />
          <circle class="scribble-1" cx="60" cy="60" r="${radius}" fill="none" stroke-dasharray="3 4" />
          <circle class="scribble-2" cx="60" cy="60" r="${radius}" fill="none" stroke-dasharray="2 6" />
        </svg>
        <div class="content">
          <div class="value">${label}</div>
          ${sublabel ? `<div class="sub">${sublabel}</div>` : ''}
        </div>
      </div>
    `;
  }
}

customElements.define('app-meter', AppMeter);
